import { patchClass } from "./utils/class";

type EventMap = Record<string, Event>;

type HandlersMap<EMap extends EventMap> = {
  [K in keyof EMap as `on${string & K}`]: null | ((ev: EMap[K]) => void);
};

// @ts-expect-error that's ok, we're forcing the typing here
interface _TypedEventTarget<EMap extends Record<string, Event>>
  extends EventTarget {
  addEventListener<K extends keyof EMap>(
    type: K,
    listener: (ev: EMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  removeEventListener<K extends keyof EMap>(
    type: K,
    listener: (ev: EMap[K]) => void,
    options?: boolean | EventListenerOptions
  ): void;
  dispatchEvent(event: Event): boolean;
}

/**
 * This patch ensure that:
 *  - instanceof EventTarget works correctly
 *    - for objects created before the patch
 *    - for objects created after the patch
 *  - addEventListener, removeEventListener and dispatchEvent work correctly
 */
function patchEventTarget(
  original: typeof globalThis.EventTarget
): typeof globalThis.EventTarget {
  type EvTargetType = {
    __evTarget: EventTarget;
  } & EventTarget;

  type Param<T extends keyof EventTarget> = Parameters<EventTarget[T]>;

  const PropertiesOptions: PropertyDescriptor = {
    configurable: false,
    enumerable: false,
    writable: false,
  };
  const methods = {
    addEventListener(this: EvTargetType, ...args: Param<"addEventListener">) {
      return this.__evTarget.addEventListener(...args);
    },
    removeEventListener(
      this: EvTargetType,
      ...args: Param<"removeEventListener">
    ) {
      return this.__evTarget.removeEventListener(...args);
    },
    dispatchEvent(this: EvTargetType, event: Event): boolean {
      // ensure the Event has the correct target and srcElement properties
      const prop = { value: this, configurable: true, writable: true };
      Object.defineProperties(event, { target: prop, srcElement: prop });
      return this.__evTarget.dispatchEvent(event);
    },
  };
  const Properties: PropertyDescriptorMap = Object.fromEntries(
    Object.entries(methods).map(([key, value]) => [
      key,
      { ...PropertiesOptions, value },
    ])
  );

  function PatchedEventTarget(this: globalThis.EventTarget) {
    // Hack to not have to implement all the EventTarget methods and properties, we just delegate to a real DOM element
    const evTarget = document.createElement("div");
    Object.defineProperties(this, Properties);
    Object.defineProperty(this, "__evTarget", {
      ...PropertiesOptions,
      value: evTarget,
    });
  }
  PatchedEventTarget.prototype = original.prototype;
  return PatchedEventTarget as unknown as typeof globalThis.EventTarget;
}

try {
  new globalThis.EventTarget();
} catch {
  // EventTarget is not supported, we need to polyfill it
  patchClass(import.meta.hot, "EventTarget", patchEventTarget);
}

type EvTargetCtor = new <
  EMap extends Record<string, Event>,
>() => _TypedEventTarget<EMap> & HandlersMap<EMap>;
export const TypedEventTarget =
  globalThis.EventTarget as unknown as EvTargetCtor;

type EventOf<T extends _TypedEventTarget<Record<string, Event>>> = {
  [K in keyof T as K extends `on${infer E}` ? E : never]: T[K] extends
    | ((ev: infer Ev) => void)
    | null
    ? Ev
    : never;
};

export function defineEventHandlers<
  EMap extends Record<string, Event>,
  T extends _TypedEventTarget<EMap>,
>(
  classCtor: new (...args: any[]) => T,
  ...eventNames: readonly (keyof EventOf<T> & string)[]
) {
  for (const eventName of eventNames) {
    const onHandlerName = `on${String(eventName)}`;
    const privateKey = `__${String(onHandlerName)}` as const;

    type Internal = { [key: typeof privateKey]: Function | null };

    function handler(this: Internal, event: Event) {
      return this[privateKey]?.call(this, event);
    }

    function get(this: Internal) {
      return this[privateKey] || null;
    }

    function set(
      this: Internal & T,
      newHandler: null | ((ev: EventOf<T>[typeof eventName]) => void)
    ) {
      if (typeof newHandler !== "function") {
        newHandler = null;
      }

      if (!this[privateKey] && newHandler) {
        this.addEventListener(eventName, handler);
      } else if (!newHandler && this[privateKey]) {
        this.removeEventListener(eventName, handler);
      }

      // ensure the internal value isn't returned from Object.keys
      Object.defineProperty(this, privateKey, {
        configurable: true,
        enumerable: false,
        writable: true,
        value: newHandler,
      });
    }

    Object.defineProperty(classCtor.prototype, onHandlerName, {
      configurable: true,
      enumerable: true,
      get,
      set,
    });
  }
}
